import * as yup from 'yup';

import { CabinEventReport } from '../../core/api/clients';

import commonValidators from '../../core/validators/commonValidators';
import flightValidators from '../../core/validators/flightValidators';

type CabinEventReportData = Pick<CabinEventReport,
'flightNumber' |
'aircraftNumber' |
'departureAirport' |
'scheduledArrivalAirport' |
'landingAirport' |
'captain' |
'firstOfficer' |
'flightAttendant' |
'cabinObserver' |
'cabinObserverInformation' |
'eventTitle' |
'locationOfEvent' |
'eventDate' |
'localEventTime' |
'eventType' |
'eventType2' |
'eventType3' |
'typeOfFlight' |
'numberOfPassengers' |
'phaseOfFlight' |
'phaseOfFlight2' |
'timeOfDay' |
'cabinLighting' |
'emergencyDeclared' |
'cabinBraced' |
'seatBeltSign' |
'emsRequested' |
'emsResponded' |
'securityRequested' |
'securityResponded' |
'emergencyMedKitUsed' |
'aedUsed' |
'portableOxygenUsed' |
'fireExtinguisherUsed' |
'aircraftTaxiToGate' |
'narrative' |
'$discriminator'
>;
const CabinEventFormValidationSchema: yup.ObjectSchema<CabinEventReportData> = yup.object({
  $discriminator: yup.string().required().meta({ step: 0 }),
  // Flight & Crew
  flightNumber: flightValidators.flightNumber({ label: 'Flight Number', step: 0, optionalIfBlankGenerated: true }),
  aircraftNumber: flightValidators.aircraftNumber({ label: 'Aircraft Number', step: 0, required: true }),

  departureAirport: flightValidators.airport({ label: 'Departure Airport', step: 0, required: true }),
  scheduledArrivalAirport: flightValidators.airport({ label: 'Scheduled Arrival Airport', step: 0, required: true }),
  landingAirport: flightValidators.airport({ label: 'Landing Airport', step: 0, required: true }),

  captain: flightValidators.crewMember({ label: 'Captain', step: 0, optionalIfBlankGenerated: true }),
  firstOfficer: flightValidators.crewMember({ label: 'First Officer', step: 0, optionalIfBlankGenerated: true }),
  flightAttendant: flightValidators.crewMember({
    label: 'Flight Attendant',
    step: 0,
    optionalIfBlankGenerated: false,
  }),

  cabinObserver: yup.string()
    .label('Cabin Observer')
    .nullable()
    .optional()
    .meta({ step: 0 }),

  cabinObserverInformation: yup.string()
    .label('Cabin Observer Information')
    .nullable()
    .optional()
    .meta({ step: 0 }),

  // Event
  eventTitle: commonValidators.nullableString({ label: 'Event Title', step: 1, required: true, maxLength: 75 }),
  locationOfEvent: commonValidators.nullableString({ label: 'Location of Event', step: 1 }),
  eventDate: commonValidators.nullableString({ label: 'Event Date', required: true, step: 1 }),

  localEventTime: commonValidators.time({
    message: 'Local Event Time must be a valid 24-hour time',
    label: 'Local Event Time',
    required: true,
    step: 1,
  }),

  eventType: commonValidators.nullableString({
    label: 'Event Type',
    required: true,
    step: 1,
  }),

  eventType2: commonValidators.nullableString({
    label: 'Event Type 2',
    required: true,
    step: 1,
  }),

  eventType3: commonValidators.nullableString({
    label: 'Event Type 3',
    required: true,
    step: 1,
  }),

  typeOfFlight: commonValidators.nullableString({
    label: 'Type of Flight',
    required: true,
    step: 1,
  }),

  numberOfPassengers: yup.number()
    .label('Number of Passengers')
    .typeError('Number of Passengers must be a number')
    .integer()
    .nullable()
    .optional()
    .meta({ step: 1 }),

  phaseOfFlight: yup.string()
    .label('Phase of Flight')
    .nullable()
    .required()
    .meta({ step: 1 }),

  phaseOfFlight2: yup.string()
    .label('Phase of Flight')
    .nullable()
    .required()
    .meta({ step: 1 }),

  timeOfDay: commonValidators.nullableString({
    label: 'Time of Day',
    required: true,
    step: 1,
  }),

  cabinLighting: commonValidators.nullableString({
    label: 'Cabin Lighting',
    required: true,
    step: 1,
  }),

  emergencyDeclared: commonValidators.radioGroupOption({ label: 'Emergency Declared', required: true, step: 1 }),
  cabinBraced: commonValidators.radioGroupOption({ label: 'Cabin Braced', required: true, step: 1 }),
  seatBeltSign: commonValidators.radioGroupOption({ label: 'Seat Belt Sign', required: true, step: 1 }),
  emsRequested: commonValidators.radioGroupOption({ label: 'EMS Requested', required: true, step: 1 }),
  emsResponded: commonValidators.radioGroupOption({ label: 'EMS Responded', required: true, step: 1 }),
  securityRequested: commonValidators.radioGroupOption({ label: 'Security Requested', required: true, step: 1 }),
  securityResponded: commonValidators.radioGroupOption({ label: 'Security Responded', required: true, step: 1 }),
  emergencyMedKitUsed: commonValidators.radioGroupOption({ label: 'Emergency Med Kit Used', required: true, step: 1 }),
  aedUsed: commonValidators.radioGroupOption({ label: 'AED Used', required: true, step: 1 }),
  portableOxygenUsed: commonValidators.radioGroupOption({ label: 'Portable Oxygen Used', required: true, step: 1 }),
  fireExtinguisherUsed: commonValidators.radioGroupOption({ label: 'Fire Extinguisher Used', required: true, step: 1 }),
  aircraftTaxiToGate: commonValidators.radioGroupOption({ label: 'Aircraft Taxi to Gate', required: true, step: 1 }),

  // Narrative
  narrative: yup.string()
    .label('Narrative')
    .nullable()
    .required()
    .meta({ step: 2 }),
});

export default CabinEventFormValidationSchema;
